import Phaser from "phaser";
import { AssetKeys } from "../../core/AssetKeys";
import { GameEvents } from "../../core/Events";
import { Player } from "../player/Player";
import { CharacterAnimator } from "../../animation/CharacterAnimator";
import { direction8FromVector, type EightDirection } from "../../animation/Direction";
import type { EnemyRole } from "./EnemyRole";
import { EnemySquad } from "./EnemySquad";
import { EnemyProjectile } from "../../combat/EnemyProjectile";

export class SkeletonEnemy extends Phaser.Physics.Arcade.Sprite {
  private nextAttackAt = 0;
  private nextDecisionAt = 0;
  private nextDodgeAt = 0;
  private health: number;
  private readonly maxHealth: number;
  private readonly animator: CharacterAnimator;
  private facing: EightDirection = "s";
  private dying = false;
  private orbitDirection: 1 | -1;
  private retreatUntil = 0;
  private readonly healthBar: Phaser.GameObjects.Rectangle;
  private readonly healthTrack: Phaser.GameObjects.Rectangle;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly target: Player,
    private readonly squad: EnemySquad,
    readonly role: EnemyRole,
    private readonly enemyProjectiles: EnemyProjectile[],
  ) {
    super(scene, x, y, AssetKeys.SkeletonAtlas, "idle_s_000");
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.maxHealth = role === "guardian" ? 290 : role === "skirmisher" ? 145 : 190;
    this.health = this.maxHealth;
    this.orbitDirection = Phaser.Math.Between(0, 1) === 0 ? -1 : 1;
    this.setCollideWorldBounds(true).setDepth(Math.round(y)).setScale(role === "guardian" ? 0.82 : 0.72);
    if (role === "guardian") this.setTint(0xd0b98d);
    if (role === "skirmisher") this.setTint(0xb9d7ee);

    this.animator = new CharacterAnimator(this, "skeleton");
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(42, 54).setOffset(27, 34);

    this.healthTrack = scene.add.rectangle(x, y - 52, 48, 4, 0x101318, 0.8).setDepth(10000);
    this.healthBar = scene.add.rectangle(x - 24, y - 52, 48, 4, 0xb43b37, 1).setOrigin(0, 0.5).setDepth(10001);
    squad.add(this);
  }

  override update(time: number): void {
    if (!this.active || this.dying || !this.target.active) return;
    const toPlayer = new Phaser.Math.Vector2(this.target.x - this.x, this.target.y - this.y);
    const distance = Math.max(1, toPlayer.length());
    const direction = toPlayer.clone().scale(1 / distance);
    const body = this.body as Phaser.Physics.Arcade.Body;
    let desired = new Phaser.Math.Vector2();

    if (time < this.retreatUntil) {
      desired.copy(direction).negate();
    } else {
      switch (this.role) {
        case "chaser":
          desired.copy(direction);
          break;
        case "flanker": {
          const tangent = new Phaser.Math.Vector2(-direction.y, direction.x).scale(this.orbitDirection);
          desired.copy(direction).scale(distance > 210 ? 0.7 : -0.15).add(tangent.scale(0.95));
          break;
        }
        case "guardian": {
          const bossX = 960;
          const bossY = 300;
          const toGuard = new Phaser.Math.Vector2(bossX - this.x, bossY - this.y);
          if (distance < 170) desired.copy(direction);
          else if (toGuard.length() > 260) desired.copy(toGuard.normalize());
          else desired.copy(direction).scale(0.35);
          break;
        }
        case "skirmisher": {
          if (distance < 220) desired.copy(direction).negate();
          else if (distance > 430) desired.copy(direction);
          else desired.set(-direction.y * this.orbitDirection, direction.x * this.orbitDirection);
          break;
        }
      }
    }

    desired.add(this.squad.separation(this).scale(1.35));
    if (desired.lengthSq() > 0) desired.normalize();
    const speed = this.role === "guardian" ? 82 : this.role === "skirmisher" ? 122 : 108;
    body.setVelocity(desired.x * speed, desired.y * speed);

    this.facing = direction8FromVector(direction.x, direction.y, this.facing);
    this.animator.updateLocomotion(this.facing, desired.lengthSq() > 0.05, time);

    if (this.role === "skirmisher") this.tryRangedAttack(time, distance);
    else this.tryMeleeAttack(time, distance);

    this.setDepth(Math.round(this.y));
    this.healthTrack.setPosition(this.x, this.y - 52).setVisible(this.health < this.maxHealth);
    this.healthBar.setPosition(this.x - 24, this.y - 52).setVisible(this.health < this.maxHealth);

    if (time >= this.nextDecisionAt) {
      this.nextDecisionAt = time + Phaser.Math.Between(700, 1300);
      if (Phaser.Math.Between(0, 100) < 24) this.orbitDirection = this.orbitDirection === 1 ? -1 : 1;
    }
  }

  dodgeFrom(worldX: number, worldY: number, time: number): void {
    if (!this.active || this.dying || time < this.nextDodgeAt || this.role === "guardian") return;
    if (Phaser.Math.Distance.Between(this.x, this.y, worldX, worldY) > 150) return;
    this.nextDodgeAt = time + Phaser.Math.Between(900, 1500);
    const away = new Phaser.Math.Vector2(this.x - worldX, this.y - worldY).normalize();
    const side = new Phaser.Math.Vector2(-away.y, away.x).scale(this.orbitDirection);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocity((away.x + side.x) * 210, (away.y + side.y) * 210);
    this.retreatUntil = time + 280;
  }

  damage(amount: number, knockbackAngle?: number): void {
    if (!this.active || this.dying) return;
    this.health -= amount;
    this.healthBar.width = 48 * Phaser.Math.Clamp(this.health / this.maxHealth, 0, 1);
    this.setTintFill(0xffffff);
    this.scene.time.delayedCall(75, () => this.active && this.clearTint());
    if (knockbackAngle !== undefined) {
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.setVelocity(Math.cos(knockbackAngle) * 180, Math.sin(knockbackAngle) * 180);
    }
    if (this.health <= 0) {
      this.dying = true;
      (this.body as Phaser.Physics.Arcade.Body).enable = false;
      this.healthTrack.destroy();
      this.healthBar.destroy();
      this.animator.playAction("death", 900, this.facing);
      this.scene.events.emit(GameEvents.EnemyDefeated, this);
      this.scene.time.delayedCall(900, () => this.destroy());
    } else {
      this.retreatUntil = this.scene.time.now + 180;
      this.animator.playAction("hit", 260, this.facing);
    }
  }

  isAlive(): boolean { return this.active && !this.dying; }

  private tryMeleeAttack(time: number, distance: number): void {
    if (distance > (this.role === "guardian" ? 94 : 78) || time < this.nextAttackAt) return;
    this.nextAttackAt = time + (this.role === "guardian" ? 1250 : 900);
    (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    this.animator.playAction("attack", 650, this.facing);
    this.scene.time.delayedCall(280, () => {
      if (this.active && this.target.active && Phaser.Math.Distance.Between(this.x, this.y, this.target.x, this.target.y) <= 100) {
        this.target.damage(this.role === "guardian" ? 58 : 35);
      }
    });
  }

  private tryRangedAttack(time: number, distance: number): void {
    if (distance < 190 || distance > 520 || time < this.nextAttackAt) return;
    this.nextAttackAt = time + Phaser.Math.Between(1700, 2300);
    this.animator.playAction("attack", 700, this.facing);
    const angle = Phaser.Math.Angle.Between(this.x, this.y, this.target.x, this.target.y);
    this.scene.time.delayedCall(320, () => {
      if (!this.active || !this.target.active) return;
      this.enemyProjectiles.push(new EnemyProjectile(this.scene, this.x, this.y - 10, angle, 28));
    });
  }
}
